/**
 * ActiveFiltersDisplay Component
 * Displays active filters as removable badge/pill tags
 * Shows applied filters with individual and bulk clear functionality
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, XCircle } from 'lucide-react';
import type { OrderFilters } from '../types/filter.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// TYPES
// ============================================

export interface ActiveFiltersDisplayProps {
  filters: OrderFilters;
  onRemoveFilter: (filterKey: keyof OrderFilters, value?: unknown) => void;
  onClearAll: () => void;
}

interface FilterBadge {
  key: string;
  label: string;
  value: string;
  filterKey: keyof OrderFilters;
  filterValue?: unknown;
}

// ============================================
// STATUS LABELS
// ============================================

const STATUS_LABELS: Record<OrderStatus, string> = {
  PENDING: 'Pending',
  CONFIRMED: 'Confirmed',
  IN_PROGRESS: 'Processing',
  DELIVERED: 'Delivered',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Format date for display in DD/MM/YYYY format
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
}

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  } else if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(0)}K`;
  }
  return `₹${amount.toLocaleString('en-IN')}`;
}

/**
 * Build array of active filter badges from filters state
 */
function buildFilterBadges(filters: OrderFilters): FilterBadge[] {
  const badges: FilterBadge[] = [];

  // Search term
  if (filters.searchTerm) {
    badges.push({
      key: 'search',
      label: 'Search',
      value: `"${filters.searchTerm}"`,
      filterKey: 'searchTerm',
    });
  }

  // Statuses
  if (filters.statuses.length > 0) {
    filters.statuses.forEach((status) => {
      badges.push({
        key: `status-${status}`,
        label: 'Status',
        value: STATUS_LABELS[status],
        filterKey: 'statuses',
        filterValue: status,
      });
    });
  }

  // Order date range
  if (filters.orderDateRange.from || filters.orderDateRange.to) {
    const from = filters.orderDateRange.from
      ? formatDate(filters.orderDateRange.from)
      : 'Start';
    const to = filters.orderDateRange.to
      ? formatDate(filters.orderDateRange.to)
      : 'End';
    badges.push({
      key: 'orderDate',
      label: 'Order Date',
      value: `${from} - ${to}`,
      filterKey: 'orderDateRange',
    });
  }

  // Expected delivery date range
  if (
    filters.expectedDeliveryDateRange.from ||
    filters.expectedDeliveryDateRange.to
  ) {
    const from = filters.expectedDeliveryDateRange.from
      ? formatDate(filters.expectedDeliveryDateRange.from)
      : 'Start';
    const to = filters.expectedDeliveryDateRange.to
      ? formatDate(filters.expectedDeliveryDateRange.to)
      : 'End';
    badges.push({
      key: 'expectedDelivery',
      label: 'Expected Delivery',
      value: `${from} - ${to}`,
      filterKey: 'expectedDeliveryDateRange',
    });
  }

  // Actual delivery date range
  if (
    filters.actualDeliveryDateRange.from ||
    filters.actualDeliveryDateRange.to
  ) {
    const from = filters.actualDeliveryDateRange.from
      ? formatDate(filters.actualDeliveryDateRange.from)
      : 'Start';
    const to = filters.actualDeliveryDateRange.to
      ? formatDate(filters.actualDeliveryDateRange.to)
      : 'End';
    badges.push({
      key: 'actualDelivery',
      label: 'Actual Delivery',
      value: `${from} - ${to}`,
      filterKey: 'actualDeliveryDateRange',
    });
  }

  // Amount range
  if (filters.amountRange.min !== null || filters.amountRange.max !== null) {
    const min = filters.amountRange.min
      ? formatCurrency(filters.amountRange.min)
      : 'Min';
    const max = filters.amountRange.max
      ? formatCurrency(filters.amountRange.max)
      : 'Max';
    badges.push({
      key: 'amount',
      label: 'Amount',
      value: `${min} - ${max}`,
      filterKey: 'amountRange',
    });
  }

  // Customer type
  if (filters.customerType !== 'ALL') {
    badges.push({
      key: 'customerType',
      label: 'Customer Type',
      value: filters.customerType,
      filterKey: 'customerType',
    });
  }

  // Location - City
  if (filters.location.city) {
    badges.push({
      key: 'city',
      label: 'City',
      value: filters.location.city,
      filterKey: 'location',
      filterValue: 'city',
    });
  }

  // Location - State
  if (filters.location.state) {
    badges.push({
      key: 'state',
      label: 'State',
      value: filters.location.state,
      filterKey: 'location',
      filterValue: 'state',
    });
  }

  return badges;
}

// ============================================
// COMPONENT
// ============================================

export function ActiveFiltersDisplay({
  filters,
  onRemoveFilter,
  onClearAll,
}: ActiveFiltersDisplayProps): JSX.Element | null {
  const badges = buildFilterBadges(filters);

  // Don't render if no active filters
  if (badges.length === 0) {
    return null;
  }

  /**
   * Handle filter removal
   */
  const handleRemove = (badge: FilterBadge) => {
    if (badge.filterKey === 'statuses' && badge.filterValue) {
      // Remove individual status from array
      onRemoveFilter('statuses', badge.filterValue);
    } else if (badge.filterKey === 'location' && badge.filterValue) {
      // Remove city or state
      onRemoveFilter('location', badge.filterValue);
    } else {
      // Remove entire filter
      onRemoveFilter(badge.filterKey);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
      <span className="text-sm font-medium text-gray-700">
        Active Filters:
      </span>

      {badges.map((badge) => (
        <Badge
          key={badge.key}
          variant="secondary"
          className="px-3 py-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
        >
          <span className="text-xs font-medium mr-1">{badge.label}:</span>
          <span className="text-xs">{badge.value}</span>
          <button
            type="button"
            onClick={() => handleRemove(badge)}
            className="ml-2 hover:text-gray-900 focus:outline-none"
            aria-label={`Remove ${badge.label} filter`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="ml-2 text-red-600 hover:text-red-700 hover:bg-red-50"
        aria-label="Clear all filters"
      >
        <XCircle className="h-4 w-4 mr-1" />
        Clear All
      </Button>
    </div>
  );
}
